import { useEffect, useState, type ReactNode } from 'react';
import type { ObjectiveId, PinnedObjectiveView, RewardConfig, RewardSnapshot } from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { objectiveCategoryTokenPrefix } from '@ui/desktop/objective-form/FormPrimitives';
import { LayoutDashboard, CalendarCheck, CalendarRange, Target, Trophy, Settings, CompassIcon, Repeat, GripVertical } from '@ui/shared/icons';
import { RailItem } from './RailItem';
import { ThemeIndicatorDot } from './ThemeIndicatorDot';

/**
 * The rail destinations (desktop-shell.md §2, superseded by owner direct
 * correction — see docs/specs/phase-4-dashboard-redesign.md's "reuse the
 * Objectives screen" decision, now overridden: Dashboard is a genuinely
 * separate destination, first in the rail). `habits` was added alongside
 * the new `src/ui/desktop/habits/` desktop screens — Habits is now a
 * first-class desktop destination, not just a phone-footer-menu link,
 * mirroring Dashboard's own precedent for promoting a screen into the rail.
 * `history` is a real `DesktopShell`-hosted route (see App.tsx) but has NO
 * rail icon of its own (§2: "wrapped in DesktopShell for routing/chrome
 * purposes but is not a rail item"), so it is deliberately absent from this
 * union.
 */
export type DesktopShellDestination = 'dashboard' | 'today' | 'habits' | 'week' | 'objectives' | 'rewards' | 'settings';

const DESTINATIONS: ReadonlyArray<{
  readonly key: DesktopShellDestination;
  readonly label: string;
  readonly icon: typeof CalendarCheck;
}> = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'today', label: 'Today', icon: CalendarCheck },
  { key: 'habits', label: 'Habits', icon: Repeat },
  { key: 'week', label: 'Week', icon: CalendarRange },
  { key: 'objectives', label: 'Objectives', icon: Target },
  { key: 'rewards', label: 'Rewards', icon: Trophy },
  { key: 'settings', label: 'Settings', icon: Settings },
];

/**
 * Shared desktop nav chrome (desktop-shell.md §1/§3/§4/§7,
 * phase-2e-nav-settings-export.md NS-1) — WIDTH SUPERSEDED per owner direct
 * correction (a live-review of the Phase 4 dashboard round): the rail now
 * shows icon AND visible label text side by side, at the
 * `layout.desktopShellRailWidthExpanded` (208px) width already reserved
 * (but unused) in tokens.css for exactly this contingency, rather than the
 * 72px icon-only rail desktop-shell.md originally shipped. The content area
 * still clears the rail's width and scrolls independently — the rail itself
 * never scrolls.
 *
 * Active-route derivation is the only "logic" here (which of six keys
 * matches `active`) — everything else is presentational; no domain/business
 * math lives in this component (dev-ui rule).
 *
 * `history` has no rail icon (§2) but IS a valid DesktopShell consumer (its
 * screen still wraps in the shell for the content-area/chrome, just with no
 * rail item ever showing as active for it) — App.tsx passes `active={null}`
 * for that route so no rail item is ever marked current for a destination
 * with no rail slot.
 */
export function DesktopShell({
  active,
  onNavigate,
  onOpenObjective,
  children,
}: {
  readonly active: DesktopShellDestination | null;
  readonly onNavigate: (destination: DesktopShellDestination) => void;
  /**
   * Phase 6 addition — powers the PINNED OBJECTIVES sidebar section's row
   * click-through (design/screens/today-desktop-v2.md §3). Optional so every
   * pre-existing `DesktopShell` call site keeps compiling unchanged; when
   * omitted, the pinned section still renders but its rows are inert (no
   * navigation target) rather than the whole section disappearing.
   */
  readonly onOpenObjective?: (objectiveId: string) => void;
  readonly children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav
        aria-label="Desktop navigation"
        style={{
          flex: 'none',
          width: 'var(--layout-desktop-shell-rail-width-expanded)',
          minHeight: '100vh',
          background: 'var(--surface-raised)',
          borderRight: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'var(--space-4)',
          paddingBottom: 'var(--space-4)',
          paddingLeft: 'var(--space-3)',
          paddingRight: 'var(--space-3)',
          overflowY: 'auto',
        }}
      >
        <SidebarIdentity />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', marginTop: 'var(--space-5)' }}>
          {DESTINATIONS.map((destination) => (
            <RailItem
              key={destination.key}
              label={destination.label}
              icon={destination.icon}
              active={active === destination.key}
              onSelect={() => onNavigate(destination.key)}
            />
          ))}
        </div>
        <PinnedObjectivesSection onOpenObjective={onOpenObjective} />
        <div style={{ flex: 1 }} />
        <div style={{ paddingLeft: 'var(--space-3)' }}>
          <ThemeIndicatorDot />
        </div>
      </nav>
      <div style={{ flex: 1, minWidth: 0, background: 'var(--surface-base)', overflowY: 'auto' }}>{children}</div>
    </div>
  );
}

/**
 * PINNED OBJECTIVES sidebar section (R8.8, Phase 6, design/screens/
 * today-desktop-v2.md §3) — new sidebar CONTENT, not a new nav paradigm
 * (D5). Lives directly beneath the existing five-item nav list. Absent
 * entirely (including its own header) when nothing is pinned — "absent, not
 * a fake empty state" (§3's explicit ruling), so an unpinned rail never
 * shows a "pin something" prompt.
 *
 * Drag/keyboard reorder (owner ask, later pass over §3's "no drag-reorder
 * control lives in the sidebar itself — too narrow" deferral): matches
 * `ActionsTable.tsx`'s exact idiom (same native HTML5 drag events on the
 * row, same `GripVertical` handle, same Space-to-pick-up / arrow-keys-to-
 * move / Space-to-drop keyboard fallback) rather than inventing a second
 * reorder mechanic for the app. The handle only claims permanent width on
 * hover/focus (`hoveredId`/focus-within), matching `PrioritiesCard.tsx`'s
 * row-level hover-reveal affordance precedent — at rest the row keeps the
 * same layout as a plain nav link.
 *
 * Reordering is NOT optimistic local state: `handleReorder` awaits
 * `setObjectivePinned` for every row whose order actually changed, then
 * relies on the surrounding `useDomainVersion()` subscription (which fires
 * on any domain mutation) to drive the next `listPinnedObjectives()` read —
 * the on-screen order is always the persisted order, never a client-only
 * guess that could drift from it.
 */
function PinnedObjectivesSection({
  onOpenObjective,
}: {
  readonly onOpenObjective?: (objectiveId: string) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [pinned, setPinned] = useState<readonly PinnedObjectiveView[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [pickedUpId, setPickedUpId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listPinnedObjectives().then((rows) => {
      if (!cancelled) setPinned(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [api, version]);

  if (pinned.length === 0) return null;

  async function persistOrder(orderedIds: readonly string[]) {
    // Reordering one item shifts every other item's relative rank, so every
    // objective whose index actually changed gets a fresh setObjectivePinned
    // call, not just the one that was dragged/moved — mirrors
    // ActionsTable's onReorder contract (a full reordered ID list in, one
    // command per changed row out).
    const byId = new Map(pinned.map((p) => [p.objectiveId as string, p]));
    await Promise.all(
      orderedIds.map((id, index) => {
        const current = byId.get(id);
        if (!current || current.pinnedOrder === index) return Promise.resolve();
        return api.setObjectivePinned(id as ObjectiveId, true, index);
      }),
    );
  }

  function moveByKeyboard(objectiveId: string, direction: -1 | 1) {
    const index = pinned.findIndex((p) => p.objectiveId === objectiveId);
    if (index < 0) return;
    const target = index + direction;
    if (target < 0 || target >= pinned.length) return;
    const next = pinned.map((p) => p.objectiveId as string);
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved as string);
    void persistOrder(next);
  }

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const ids = pinned.map((p) => p.objectiveId as string);
    const fromIndex = ids.indexOf(dragId);
    const toIndex = ids.indexOf(targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDragId(null);
      return;
    }
    const next = [...ids];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved as string);
    void persistOrder(next);
    setDragId(null);
  }

  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <div
        style={{
          padding: '0 var(--space-2)',
          fontSize: 'var(--type-label-size)',
          lineHeight: 'var(--type-label-line)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        Pinned objectives
      </div>
      <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {pinned.map((objective) => (
          <PinnedObjectiveRow
            key={objective.objectiveId}
            objective={objective}
            picking={pickedUpId === objective.objectiveId}
            onOpenObjective={onOpenObjective}
            onDragStart={() => setDragId(objective.objectiveId)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(objective.objectiveId)}
            onTogglePicked={() => setPickedUpId((cur) => (cur === objective.objectiveId ? null : objective.objectiveId))}
            onMoveUp={() => moveByKeyboard(objective.objectiveId, -1)}
            onMoveDown={() => moveByKeyboard(objective.objectiveId, 1)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One PINNED OBJECTIVES row. The grip handle is a sibling button, not a
 * descendant of the title button (buttons can't nest) — the row's click-
 * through target keeps the accessible name "<title>" exactly as before this
 * change; the handle carries its own "Reorder <title>" label, matching
 * ActionsTable's `ActionRow` handle-labeling convention.
 */
function PinnedObjectiveRow({
  objective,
  picking,
  onOpenObjective,
  onDragStart,
  onDragOver,
  onDrop,
  onTogglePicked,
  onMoveUp,
  onMoveDown,
}: {
  readonly objective: PinnedObjectiveView;
  readonly picking: boolean;
  readonly onOpenObjective?: (objectiveId: string) => void;
  readonly onDragStart: () => void;
  readonly onDragOver: (e: React.DragEvent) => void;
  readonly onDrop: () => void;
  readonly onTogglePicked: () => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  function handleHandleKeyDown(e: React.KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onTogglePicked();
      return;
    }
    if (!picking) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onMoveUp();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onMoveDown();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onTogglePicked();
    }
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`pinned-objective-row-${objective.objectiveId}`}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
    >
      {/* Handle only claims permanent width on hover/focus/pick-up so the
          rail's fixed 208px width isn't spent on a control that's idle most
          of the time — matches PrioritiesCard's row-hover affordance
          pattern, adapted from opacity-only (ActionsTable has room to spare)
          to width-collapse (the rail doesn't). */}
      <button
        type="button"
        aria-label={`Reorder ${objective.title}${picking ? ' (picked up — use arrow keys to move, Space to drop)' : ''}`}
        aria-pressed={picking}
        onKeyDown={handleHandleKeyDown}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{
          width: hovered || picking ? '16px' : '0px',
          minWidth: hovered || picking ? '16px' : '0px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: picking ? 'var(--hue-primary)' : 'var(--text-muted)',
          cursor: 'grab',
          flex: 'none',
          transition: 'width var(--dur-fast) var(--ease-standard)',
        }}
      >
        <GripVertical size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => onOpenObjective?.(objective.objectiveId)}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          minHeight: '44px',
          padding: '0 var(--space-2)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-secondary)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 500,
          textAlign: 'left',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            flex: 'none',
            background: (() => {
              const prefix = objectiveCategoryTokenPrefix(objective.category);
              return prefix ? `var(${prefix}-vivid)` : 'var(--text-muted)';
            })(),
          }}
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{objective.title}</span>
      </button>
    </div>
  );
}

/**
 * The persistent "Compass" wordmark + ambient level-identity subtitle at the
 * top of the sidebar (owner direct correction: this lived inside
 * `DashboardScreen.desktop.tsx`'s own header in the previous round, but the
 * reference places it in the sidebar itself, visible on every desktop
 * screen, not just Dashboard — matching how `LevelFooterChip` is already a
 * persistent ambient fixture on the phone Today footer, not a
 * Dashboard-only display). Fetches its own reward snapshot rather than
 * threading one down through every screen, mirroring `ThemeIndicatorDot`'s
 * own self-contained fetch in this same shell.
 */
function SidebarIdentity() {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [snapshot, setSnapshot] = useState<RewardSnapshot | null>(null);
  const [config, setConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.getRewardSnapshot(), api.getRewardConfig()]).then(([s, c]) => {
      if (!cancelled) {
        setSnapshot(s);
        setConfig(c);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [api, version]);

  const identity = snapshot ? resolveLevelIdentity(snapshot, config).identity : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '0 var(--space-2)' }}>
      <span
        aria-hidden="true"
        style={{
          width: '40px',
          height: '40px',
          borderRadius: 'var(--radius-md)',
          background: 'color-mix(in srgb, var(--hue-primary-vivid) 16%, var(--surface-hover))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <CompassIcon size={20} color="var(--hue-primary)" aria-hidden="true" />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 700, color: 'var(--text-primary)' }}>Compass</div>
        {identity && (
          <div
            role="img"
            aria-label={`Level ${snapshot?.level}, ${identity.name}`}
            style={{
              fontSize: 'var(--type-label-size)',
              letterSpacing: 'var(--type-label-tracking)',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {identity.name}
          </div>
        )}
      </div>
    </div>
  );
}
